import type { PoiStyle, SponsorRingStyle, ShadowDirection } from "../themes/types";

/**
 * Icon path data for dynamic marker generation
 */
export interface MarkerIconData {
  /** SVG d path attribute */
  path: string;
  /** viewBox dimensions (e.g., "0 0 512 512") */
  viewBox: string;
}

// SponsorRingConfig is now an alias for SponsorRingStyle from types.ts
export type SponsorRingConfig = SponsorRingStyle;

// Re-export defaults from shared location (server-safe)
export { DEFAULT_SPONSOR_RING_STYLES, LEGACY_TIER_COLORS, getRingStyleForPosition } from "../themes/sponsor-ring-defaults";

/**
 * Generate a dynamic marker image for a POI category
 *
 * Creates a canvas-rendered marker with:
 * - Optional sponsor ring (drawn first, behind everything)
 * - Circle background with theme color
 * - Circle stroke with theme stroke color
 * - Icon rendered with theme icon color
 * - Shadow respecting global shadow direction
 *
 * @param poiStyle - POI style configuration from theme
 * @param iconData - SVG path data for the category icon
 * @param circleColorOverride - Optional color override for circle background (per-category)
 * @param sponsorRing - Optional sponsor ring config (bakes ring into sprite)
 * @param shadowDirection - Optional global shadow direction vector
 * @returns ImageData for use with MapLibre's addImage()
 */
export function generateDynamicMarker(
  poiStyle: PoiStyle,
  iconData: MarkerIconData,
  circleColorOverride?: string,
  sponsorRing?: SponsorRingConfig,
  shadowDirection?: ShadowDirection
): ImageData {
  // Use device pixel ratio for Retina sharpness
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio : 1;

  // POI-specific direction takes priority, then global, then default (straight down)
  const dir = poiStyle.shadowDirection ?? shadowDirection;
  const dirX = dir?.x ?? 0;
  const dirY = dir?.y ?? 1;
  const shadowOffsetX = poiStyle.shadowOffsetY * dirX;
  const shadowOffsetYActual = poiStyle.shadowOffsetY * dirY;

  // Canvas size based on circle radius (which defines the marker size)
  const markerSize = poiStyle.circleRadius * 2;

  // When sponsor ring present, enlarge canvas to fit ring + glow
  // Scale SVG-space values to marker-space: markerSize maps to the 40px circle in 55px SVG
  const svgScale = markerSize / 40;
  let totalSize = markerSize;
  let ringRadius = 0;
  let ringStrokeWidth = 0;
  let ringGlowBlur = 0;

  if (sponsorRing) {
    ringRadius = poiStyle.circleRadius * sponsorRing.radiusRatio;
    ringStrokeWidth = sponsorRing.strokeWidth * svgScale;
    ringGlowBlur = sponsorRing.glowBlur * svgScale;
    // Canvas must fit: ring outer edge + glow
    const ringOuterEdge = ringRadius + ringStrokeWidth / 2 + ringGlowBlur * 2;
    totalSize = Math.ceil(ringOuterEdge * 2);
  }

  // Enlarge canvas for POI shadow (blur extends in all directions, offset shifts directionally)
  const shadowPadding = poiStyle.shadowBlur > 0
    ? Math.ceil(poiStyle.shadowBlur * 2 + Math.max(Math.abs(shadowOffsetX), Math.abs(shadowOffsetYActual)))
    : 0;
  totalSize += shadowPadding * 2;

  const canvasSize = Math.ceil(totalSize * dpr);

  // Create offscreen canvas
  const canvas = document.createElement("canvas");
  canvas.width = canvasSize;
  canvas.height = canvasSize;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Failed to get 2D canvas context");
  }

  // Scale for device pixel ratio
  ctx.scale(dpr, dpr);

  const centerX = totalSize / 2;
  const centerY = totalSize / 2;
  const radius = poiStyle.circleRadius - poiStyle.circleStrokeWidth / 2;

  // Draw sponsor ring (behind everything)
  if (sponsorRing) {
    // Glow: draw ring stroke with shadow
    ctx.save();
    ctx.shadowColor = sponsorRing.color;
    ctx.shadowBlur = ringGlowBlur * dpr;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.globalAlpha = sponsorRing.glowOpacity;
    ctx.beginPath();
    ctx.arc(centerX, centerY, ringRadius, 0, Math.PI * 2);
    ctx.strokeStyle = sponsorRing.color;
    ctx.lineWidth = ringStrokeWidth;
    ctx.stroke();
    ctx.restore();

    // Ring stroke (full opacity, no shadow)
    ctx.beginPath();
    ctx.arc(centerX, centerY, ringRadius, 0, Math.PI * 2);
    ctx.strokeStyle = sponsorRing.color;
    ctx.lineWidth = ringStrokeWidth;
    ctx.stroke();
  }

  // Draw shadow if configured
  if (poiStyle.shadowBlur > 0) {
    ctx.shadowColor = poiStyle.shadowColor;
    ctx.shadowBlur = poiStyle.shadowBlur * dpr;
    ctx.shadowOffsetX = shadowOffsetX * dpr;
    ctx.shadowOffsetY = shadowOffsetYActual * dpr;
  }

  // Draw circle background
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.fillStyle = circleColorOverride ?? poiStyle.circleColor;
  ctx.fill();

  // Reset shadow for stroke
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;

  // Draw circle stroke
  ctx.lineWidth = poiStyle.circleStrokeWidth;
  ctx.strokeStyle = poiStyle.circleStrokeColor;
  ctx.stroke();

  // Parse viewBox for icon scaling
  const viewBoxParts = iconData.viewBox.split(" ").map(Number);
  const vbWidth = viewBoxParts[2] || 512;
  const vbHeight = viewBoxParts[3] || 512;

  // Calculate icon position and scale
  const iconSize = poiStyle.iconSize;
  const iconScale = iconSize / Math.max(vbWidth, vbHeight);
  const iconOffsetX = centerX - (vbWidth * iconScale) / 2;
  const iconOffsetY = centerY - (vbHeight * iconScale) / 2;

  // Draw icon using Path2D
  ctx.save();
  ctx.translate(iconOffsetX, iconOffsetY);
  ctx.scale(iconScale, iconScale);

  const path = new Path2D(iconData.path);
  ctx.fillStyle = poiStyle.iconColor;
  ctx.fill(path);

  ctx.restore();

  // Return ImageData for MapLibre
  return ctx.getImageData(0, 0, canvasSize, canvasSize);
}
